<template>
	<form
		class="integrations-custom-scripts-event-deletion-form"
		@submit.prevent="onSubmit"
	>
		<SuiContentBlock
			class="integrations-custom-scripts-event-deletion-form__title"
		>
			<template #header>Remove calendar events</template>
		</SuiContentBlock>
		<div class="integrations-custom-scripts-event-deletion-form__content">
			<SuiSelectListSwitch
				v-model="selectedView"
				class="integrations-custom-scripts-event-deletion-form__switch"
				:items="switchItems"
				@update:model-value="updateIsRunValidated"
			/>

			<div
				v-if="selectedView === SWITCH_ITEM.ID_LIST"
				class="integrations-custom-scripts-event-deletion-form__row"
			>
				<label
					class="integrations-custom-scripts-event-deletion-form__label"
					for="eventList"
				>
					Event id list (separated by comma):
				</label>
				<textarea
					id="eventList"
					v-model="idList"
					class="integrations-custom-scripts-event-deletion-form__event-list"
					name="eventList"
					placeholder="e.g. 12345,54321"
					@input="updateIsRunValidated"
				/>
			</div>

			<div v-if="selectedView === SWITCH_ITEM.CUSTOM">
				<div
					class="integrations-custom-scripts-event-deletion-form__row"
				>
					<label
						class="integrations-custom-scripts-event-deletion-form__label"
						for="activitySource"
					>
						Activity source:
					</label>
					<SuiSelect
						v-model="activitySource"
						:options="possibleActivitySource"
						hide-clear-button
						placeholder=" - activity source - "
						:min-width="MIN_INPUT_WIDTH"
						:variant="v.activitySource.$error ? 'error' : undefined"
						:message="
							v.activitySource.$error
								? v.activitySource.$errors[0]?.$message.toString()
								: undefined
						"
						class="integrations-custom-scripts-event-deletion-form__input"
						@update:model-value="onActivitySourceUpdate"
					/>
				</div>

				<div
					class="integrations-custom-scripts-event-deletion-form__row"
				>
					<label
						class="integrations-custom-scripts-event-deletion-form__label"
						for="startDate"
					>
						{{ $t('start_date') }}:
					</label>
					<AppDatePicker
						class="integrations-custom-scripts-event-deletion-form__input"
						name="startDate"
						:model-value="startDateModify"
						:label="$t('start_date')"
						:variant="
							v.startDateModify.$error ? 'error' : undefined
						"
						:message="
							v.startDateModify.$error
								? v.startDateModify.$errors[0]?.$message.toString()
								: undefined
						"
						@update:model-value="onStartDateUpdate"
					/>
				</div>

				<div
					class="integrations-custom-scripts-event-deletion-form__row"
				>
					<label
						class="integrations-custom-scripts-event-deletion-form__label"
						for="endDate"
					>
						{{ $t('end_date') }}:
					</label>
					<AppDatePicker
						class="integrations-custom-scripts-event-deletion-form__input"
						name="endDate"
						:model-value="endDateModify"
						:label="$t('end_date')"
						:variant="v.endDateModify.$error ? 'error' : undefined"
						:message="
							v.endDateModify.$error
								? v.endDateModify.$errors[0]?.$message.toString()
								: undefined
						"
						@update:model-value="onEndDateUpdate"
					/>
				</div>

				<div
					class="integrations-custom-scripts-event-deletion-form__row"
				>
					<label
						class="integrations-custom-scripts-event-deletion-form__label"
						for="createdDate"
					>
						{{ $t('created_date') }}:
					</label>
					<AppDatePicker
						class="integrations-custom-scripts-event-deletion-form__input"
						name="createdDate"
						:model-value="createdDateModify"
						:label="$t('created_date')"
						:variant="
							v.createdDateModify.$error ? 'error' : undefined
						"
						:message="
							v.createdDateModify.$error
								? v.createdDateModify.$errors[0]?.$message.toString()
								: undefined
						"
						@update:model-value="onCreatedDateUpdate"
					/>
				</div>

				<div
					class="integrations-custom-scripts-event-deletion-form__row"
				>
					<label
						class="integrations-custom-scripts-event-deletion-form__label"
						for="endDate"
					>
						Choose user:
					</label>
					<UserAutoSuggest
						v-model="userId"
						class="integrations-custom-scripts-event-deletion-form__input"
						:active-item="selectedUserItem"
						:min-width="MIN_INPUT_WIDTH"
						:variant="v.userId.$error ? 'error' : undefined"
						:message="
							v.activitySource.$error
								? v.activitySource.$errors[0]?.$message.toString()
								: undefined
						"
						@input="onUserInput"
					/>
				</div>
			</div>
			<div
				class="integrations-custom-scripts-event-deletion-form__controls"
			>
				<SuiButton
					type="submit"
					:is-loading="loading"
					:is-disabled="loading"
					variant="primary"
				>
					{{ isRunValidated ? 'Execute' : 'Dry run' }}
				</SuiButton>
			</div>
			<div
				v-if="outputMessage"
				class="integrations-custom-scripts-event-deletion-form__output-message"
			>
				<h3>Output:</h3>
				<p>{{ outputMessage }}</p>
			</div>
		</div>
	</form>
</template>

<script setup lang="ts">
import { useTranslation } from '@/composables';
import { AppDatePicker } from '@/features/App';
import { useMessage } from '@/features/Message';
import {
	useIntegrationsCustomScriptsExecutionMutation,
	type UserAutoSuggestQuery,
} from '@/generated/graphql';
import {
	SuiButton,
	SuiContentBlock,
	SuiSelect,
	SuiSelectListSwitch,
} from '@developers/scoro-ui';
import { useVuelidate } from '@vuelidate/core';
import { computed, ref } from 'vue';
import { UserAutoSuggest } from '@/features/User';
import { requiredUnless } from '@vuelidate/validators';
import type { ValueOf } from '@/types';
import { DEFAULT_DATE, MIN_INPUT_WIDTH, SWITCH_ITEM } from './constants';

const activitySource = ref();
const startDateModify = ref('');
const endDateModify = ref('');
const createdDateModify = ref('');
const userId = ref();
const idList = ref('');
const outputMessage = ref('');
const isRunValidated = ref(false);
const selectedUserItem = ref();

const switchItems = [
	{
		value: SWITCH_ITEM.ID_LIST,
		label: 'Delete events by id list',
	},
	{
		value: SWITCH_ITEM.CUSTOM,
		label: 'Delete event by custom criteria',
	},
];
const selectedView = ref<ValueOf<typeof SWITCH_ITEM>>(SWITCH_ITEM.ID_LIST);

const possibleActivitySource = computed(() => [
	{
		id: 'GOOGLE_CALENDAR',
		title: 'Google Calendar',
	},
	{
		id: 'EXCHANGE',
		title: 'Exchange',
	},
]);

const { addSuccessMessage, addErrorMessage } = useMessage();
const { t } = useTranslation();

const { mutate, loading } = useIntegrationsCustomScriptsExecutionMutation({});

const isNotCustomView = () =>
	Boolean(selectedView.value !== SWITCH_ITEM.CUSTOM);

const rules = computed(() => ({
	startDateModify: {},
	endDateModify: {},
	createdDateModify: {},
	activitySource: {
		requiredIf: requiredUnless(isNotCustomView),
	},
	userId: {
		requiredIf: requiredUnless(isNotCustomView),
	},
}));

const v = useVuelidate(rules, {
	startDateModify,
	endDateModify,
	createdDateModify,
	activitySource,
	userId,
});

const updateIsRunValidated = () => {
	isRunValidated.value = false;
	outputMessage.value = '';
};

const onUserInput = (
	value: UserAutoSuggestQuery['UsersList'][0] | undefined
) => {
	selectedUserItem.value = value;
	userId.value = value?.id;
	updateIsRunValidated();
};

const onStartDateUpdate = (value: string) => {
	startDateModify.value = value;
	updateIsRunValidated();
};

const onEndDateUpdate = (value: string) => {
	endDateModify.value = value;
	updateIsRunValidated();
};

const onCreatedDateUpdate = (value: string) => {
	createdDateModify.value = value;
	updateIsRunValidated();
};

const onActivitySourceUpdate = (value: string) => {
	activitySource.value = value;
	updateIsRunValidated();
};

const onSubmit = async () => {
	if (selectedView.value === SWITCH_ITEM.CUSTOM) {
		const valid = await v.value.$validate();
		if (!valid) {
			addErrorMessage({
				message: t('form_save_error'),
			});
			return;
		}
	} else if (!idList.value) {
		addErrorMessage({
			message: 'Event id list can not be empty',
		});
		return;
	}

	if (startDateModify.value > endDateModify.value) {
		addErrorMessage({
			message: t('end_datetime_cannot_be_before_start_datetime'),
		});
		return;
	}

	if (
		(!startDateModify.value || !endDateModify.value) &&
		!createdDateModify.value
	) {
		addErrorMessage({
			message: t('created_date_or_start_and_end_date_required'),
		});
		return;
	}

	const input =
		selectedView.value === SWITCH_ITEM.ID_LIST
			? {
					idList: idList.value,
					scriptAction: !isRunValidated.value
						? 'validate'
						: 'execute',
					scriptSource: 'deleteEvents',
				}
			: {
					activitySource: activitySource.value,
					startDate: startDateModify.value || DEFAULT_DATE,
					endDate: endDateModify.value || DEFAULT_DATE,
					createdDate: createdDateModify.value || DEFAULT_DATE,
					userId: parseInt(userId.value),
					scriptAction: !isRunValidated.value
						? 'validate'
						: 'execute',
					scriptSource: 'deleteEvents',
				};

	mutate({
		input,
	}).then((response) => {
		if (isRunValidated.value) {
			addSuccessMessage({
				message: 'Script is successfully executed',
			});
		}

		if (
			response &&
			response.data &&
			response.data.CustomScriptExecution &&
			response.data.CustomScriptExecution.length > 0
		) {
			outputMessage.value =
				response?.data.CustomScriptExecution.toString();
			isRunValidated.value = true;
		}
	});

	return;
};
</script>

<style scoped lang="scss">
.integrations-custom-scripts-event-deletion-form {
	display: flex;
	flex-direction: column;
	padding: 4rem 1rem 2rem;

	&__title {
		color: $neutral-70;
		margin-bottom: 2rem;
	}

	&__content {
		background-color: $blue-10;
	}

	&__switch {
		background-color: $blue-10;
	}

	&__row {
		display: flex;
		align-items: center;
		gap: 2rem;
		padding: 1rem;
	}

	&__label {
		display: flex;
		align-items: center;
		min-width: 18.75rem;
	}

	&__event-list {
		min-width: 50rem;
		min-height: 12.5rem;
	}

	&__input {
		min-width: 26.25rem;
	}

	&__controls {
		background-color: $blue-10;
		display: flex;
		justify-content: center;
		gap: 1rem;
		padding: 2.5rem 3rem 1.5rem;
	}

	&__output-message {
		background-color: $neutral-00;
		border: 1px solid $neutral-30;
		margin-top: 2rem;
		padding: 1rem;
	}
}
</style>
